import React from 'react'
import { PriceDisplayProps, StatusBadgeProps, TyreSizeDisplayProps, StockStatus } from './types'

// Price Display Component
export const PriceDisplay: React.FC<PriceDisplayProps> = ({
  price,
  originalPrice,
  dealPrice,
  currency = 'R',
  size = 'md'
}) => {
  const currentPrice = dealPrice || price
  const hasDiscount = dealPrice && dealPrice < price
  
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl'
  }
  
  const largeSizeClasses = {
    sm: 'text-base',
    md: 'text-xl',
    lg: 'text-2xl'
  }

  return (
    <div className="flex items-baseline space-x-2">
      <span className={`font-bold ${hasDiscount ? 'text-orange-600' : 'text-gray-900'} ${largeSizeClasses[size]}`}>
        {currency}{currentPrice.toLocaleString()}
      </span>
      {hasDiscount && originalPrice && (
        <span className={`text-gray-500 line-through font-medium ${sizeClasses[size]}`}>
          {currency}{originalPrice.toLocaleString()}
        </span>
      )}
      {hasDiscount && (
        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-semibold">
          -{Math.round(((price - currentPrice) / price) * 100)}%
        </span>
      )}
    </div>
  )
}

// Status Badge Component
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  quantity,
  size = 'md'
}) => {
  const getStatusConfig = (status: StockStatus) => {
    switch (status) {
      case 'in_stock':
        return {
          bg: 'bg-success-100',
          text: 'text-success-800',
          label: 'In Stock'
        }
      case 'low_stock':
        return {
          bg: 'bg-warning-100',
          text: 'text-warning-800',
          label: 'Low Stock'
        }
      case 'out_of_stock':
        return {
          bg: 'bg-error-100',
          text: 'text-error-800',
          label: 'Out of Stock'
        }
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          label: 'Unknown'
        }
    }
  }

  const config = getStatusConfig(status)
  const sizeClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'
  
  return (
    <div className={`inline-flex items-center ${config.bg} ${config.text} ${sizeClasses} rounded-full font-medium`}>
      <div className={`w-2 h-2 rounded-full mr-2 ${status === 'in_stock' ? 'bg-success-500' : status === 'low_stock' ? 'bg-warning-500' : 'bg-error-500'}`} />
      {config.label}
      {quantity !== undefined && status !== 'out_of_stock' && (
        <span className="ml-1">({quantity})</span>
      )}
    </div>
  )
}

// Tyre Size Display Component
export const TyreSizeDisplay: React.FC<TyreSizeDisplayProps> = ({
  width,
  aspectRatio,
  rimDiameter,
  size = 'md',
  className = ''
}) => {
  const tyreSize = `${width}/${aspectRatio}R${rimDiameter}`
  
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-2xl'
  }
  
  return (
    <div className={`font-mono font-bold text-gray-900 tracking-wider ${sizeClasses[size]} ${className}`}>
      {tyreSize}
    </div>
  )
}

// Loading Skeleton Component
export const LoadingSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
)

// Tyre Card Skeleton
export const TyreCardSkeleton: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div className={`bg-white rounded-xl ${compact ? 'p-3' : 'p-4'} shadow-sm border border-gray-100`}>
    <div className="space-y-3">
      {/* Image skeleton */}
      <LoadingSkeleton className={`w-full ${compact ? 'h-24' : 'h-32'} rounded-lg`} />
      
      {/* Tyre size skeleton */}
      <LoadingSkeleton className="h-6 w-24" />
      
      {/* Brand name skeleton */}
      <LoadingSkeleton className="h-5 w-32" />
      
      {/* Price skeleton */}
      <LoadingSkeleton className="h-6 w-20" />
      
      {/* Status badge skeleton */}
      <LoadingSkeleton className="h-6 w-16 rounded-full" />
      
      {!compact && (
        <>
          {/* Action buttons skeleton */}
          <div className="flex space-x-2 pt-2">
            <LoadingSkeleton className="h-8 w-20 rounded-lg" />
            <LoadingSkeleton className="h-8 w-24 rounded-lg" />
          </div>
        </>
      )}
    </div>
  </div>
)

// Business Card Skeleton
export const BusinessCardSkeleton: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div className={`bg-white rounded-xl ${compact ? 'p-3' : 'p-4'} shadow-sm border border-gray-100`}>
    <div className="space-y-3">
      {/* Header with logo */}
      <div className="flex items-start space-x-3">
        <LoadingSkeleton className="w-12 h-12 rounded-lg" />
        <div className="flex-1 space-y-2">
          <LoadingSkeleton className="h-5 w-32" />
          <LoadingSkeleton className="h-4 w-24" />
        </div>
      </div>
      
      {/* Services skeleton */}
      <div className="flex space-x-2">
        <LoadingSkeleton className="h-6 w-16 rounded-full" />
        <LoadingSkeleton className="h-6 w-20 rounded-full" />
        <LoadingSkeleton className="h-6 w-14 rounded-full" />
      </div>
      
      {!compact && (
        <>
          {/* Contact info skeleton */}
          <div className="space-y-2">
            <LoadingSkeleton className="h-4 w-40" />
            <LoadingSkeleton className="h-4 w-36" />
          </div>
          
          {/* Action buttons skeleton */}
          <div className="flex space-x-2 pt-2">
            <LoadingSkeleton className="h-8 w-16 rounded-lg" />
            <LoadingSkeleton className="h-8 w-20 rounded-lg" />
          </div>
        </>
      )}
    </div>
  </div>
)

// Image with fallback
export const OptimizedImage: React.FC<{
  src: string
  alt: string
  className?: string
  fallback?: string
  onLoad?: () => void
  onError?: () => void
}> = ({ 
  src, 
  alt, 
  className = '', 
  fallback = '/images/tyre-placeholder.png',
  onLoad,
  onError 
}) => {
  const [imageSrc, setImageSrc] = React.useState(src)
  const [isLoading, setIsLoading] = React.useState(true)
  const [hasError, setHasError] = React.useState(false)

  const handleLoad = () => {
    setIsLoading(false)
    onLoad?.()
  }

  const handleError = () => {
    setHasError(true)
    setIsLoading(false)
    setImageSrc(fallback)
    onError?.()
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {isLoading && (
        <LoadingSkeleton className="absolute inset-0" />
      )}
      <img
        src={imageSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
      />
      {hasError && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <div className="text-gray-600 text-center">
            <div className="text-2xl mb-1">🔧</div>
            <div className="text-xs">No Image</div>
          </div>
        </div>
      )}
    </div>
  )
}

// Format distance utility
export const formatDistance = (distanceKm?: number): string => {
  if (!distanceKm) return ''
  
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)}m away`
  } else if (distanceKm < 10) {
    return `${distanceKm.toFixed(1)}km away`
  } else {
    return `${Math.round(distanceKm)}km away`
  }
}

// Format rating utility
export const formatRating = (rating?: number, reviewCount?: number): string => {
  if (!rating) return 'No ratings'
  
  const stars = '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating))
  const reviewText = reviewCount ? ` (${reviewCount} review${reviewCount !== 1 ? 's' : ''})` : ''
  
  return `${stars} ${rating.toFixed(1)}${reviewText}`
}